/**
 * `/opportunities` — the list view, and the funnel board behind `?view=board`.
 *
 * Everything that decides what is on screen is in the URL: filters, sort,
 * page, the free-text term, and which view. The one thing that is in the URL
 * but **never in the request** is the term — §9 puts it in a `POST` body,
 * because API Gateway's access-log format excludes the query string precisely
 * so search terms never reach it.
 *
 * Saved views are `localStorage`; there is no `saved_views` table. The three
 * built-ins are URL presets.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Columns3, Download, KanbanSquare, Plus, Save, Table2, Trash2 } from 'lucide-react';
import { useMe } from '@/auth/useMe';
import { api, ApiError, apiRaw } from '@/lib/api';
import { useApiPost, useApiQuery, type Paged } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import {
  loadSavedViews,
  storeSavedViews,
  toQueryString,
  toRequest,
  useListState,
  type SavedView,
} from '@/lib/list-filters';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { FilterBar } from './FilterBar';
import { OpportunityTable } from './OpportunityTable';
import { FunnelBoard } from './FunnelBoard';
import {
  COLUMNS,
  clampColumnWidth,
  loadColumnPrefs,
  storeColumnPrefs,
  type ColumnPrefs,
  type Row,
} from './columns';
import { useReferenceData } from '@/lib/reference';
import { FORECAST_CATEGORIES } from '@menlo/shared';
import { humanize } from '@/lib/format';

const PAGE_SIZE = 100;

export function OpportunityList(): React.ReactElement {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { state, params, setFilter, setMany, clearFilters, activeFilterCount } = useListState();
  const request = useMemo(() => toRequest(state), [state]);

  /**
   * Which columns, and how wide — one stored value, because they are written
   * together and a half-written one loses a user's selection.
   */
  const [columnPrefs, setColumnPrefs] = useState<ColumnPrefs>(() => loadColumnPrefs());
  const { ids: columnIds, widths: columnWidths } = columnPrefs;
  const [chooserOpen, setChooserOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => loadSavedViews());

  const isManager = me?.tier === 'Manager' || me?.tier === 'Admin';

  /**
   * A term means `POST /api/opportunities/search` with the term in the body;
   * no term means the ordinary `GET` list. Two hooks rather than one so the
   * §9 rule is visible at the call site instead of buried in a helper.
   */
  const listQuery = useApiQuery<Paged<Row>>(
    ['opportunities', 'list', request],
    request.term === null ? `/api/opportunities?${toQueryString(request, PAGE_SIZE)}` : null,
    { placeholderData: (previous) => previous },
  );

  const searchQuery = useApiPost<Paged<Row>>(
    ['opportunities', 'search', request],
    '/api/opportunities/search',
    {
      ...request.filters,
      term: request.term ?? '',
      page: request.page,
      pageSize: PAGE_SIZE,
      ...(request.sort === null ? {} : { sort: request.sort }),
    },
    { enabled: request.term !== null, placeholderData: (previous) => previous },
  );

  const active = request.term === null ? listQuery : searchQuery;
  const page = active.data;
  const rows = page?.items ?? [];

  useEffect(() => {
    setSelected(new Set());
  }, [params]);

  const onSort = useCallback(
    (sortKey: string) => {
      const next = state.sort === sortKey ? `-${sortKey}` : sortKey;
      setMany({ sort: next });
    },
    [state.sort, setMany],
  );

  const savePrefs = useCallback((next: ColumnPrefs): void => {
    setColumnPrefs(next);
    storeColumnPrefs(next);
  }, []);

  /** Adding a column puts it back in the canonical order, not at the end. */
  const toggleColumn = (id: string): void => {
    const nextIds = columnIds.includes(id)
      ? columnIds.filter((existing) => existing !== id)
      : COLUMNS.map((column) => column.id).filter(
          (columnId) => columnId === id || columnIds.includes(columnId),
        );
    // A width outlives the column being switched off, so switching it back on
    // restores the width the user chose rather than the default.
    savePrefs({ ids: nextIds, widths: columnWidths });
  };

  /** Once per gesture, when the edge is released — not once per pixel. */
  const resizeColumn = (id: string, width: number): void => {
    savePrefs({ ids: columnIds, widths: { ...columnWidths, [id]: clampColumnWidth(width) } });
  };

  return (
    <div className="space-y-3">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-baseline gap-3">
          <h1 className="text-lg font-semibold">Opportunities</h1>
          <p className="text-xs text-muted-foreground">
            {active.isLoading ? 'Loading…' : `${page?.total ?? 0} matching`}
            {page?.countedOverView === true && (
              <span title="A filter or sort reads a derived column, so the count evaluates v_opportunity_effective rather than the base table.">
                {' '}
                · counted over the view
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <ViewToggle
            view={state.view}
            onChange={(view) => setMany({ view: view === 'table' ? null : view })}
          />
          <Button variant="outline" size="sm" onClick={() => setChooserOpen(true)}>
            <Columns3 className="h-3.5 w-3.5" aria-hidden="true" />
            Columns
          </Button>
          <ExportButton request={request} />
          <Button size="sm" onClick={() => navigate('/opportunities/new')}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            New opportunity
          </Button>
        </div>
      </header>

      <SavedViewBar
        savedViews={savedViews}
        currentQuery={params.toString()}
        myUserId={me?.user.id ?? null}
        onApply={(query) => setSearchParams(new URLSearchParams(query), { replace: false })}
        onSave={(view) => {
          const next = [...savedViews.filter((existing) => existing.id !== view.id), view];
          setSavedViews(next);
          storeSavedViews(next);
        }}
        onDelete={(id) => {
          const next = savedViews.filter((view) => view.id !== id);
          setSavedViews(next);
          storeSavedViews(next);
        }}
      />

      <FilterBar
        state={state}
        setFilter={setFilter}
        setMany={setMany}
        clearFilters={clearFilters}
        activeFilterCount={activeFilterCount}
      />

      {active.error !== null && (
        <p className="rounded-md border border-destructive/40 bg-red-50 p-3 text-sm text-red-900">
          {active.error.message}
        </p>
      )}

      {isManager && selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-md border bg-accent/50 p-2 text-sm">
          <span>{selected.size} selected</span>
          <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)}>
            Bulk actions
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear selection
          </Button>
        </div>
      )}

      {state.view === 'board' ? (
        <FunnelBoard rows={rows} />
      ) : (
        <OpportunityTable
          rows={rows}
          columnIds={columnIds}
          columnWidths={columnWidths}
          onResizeColumn={resizeColumn}
          sort={state.sort}
          onSort={onSort}
          selected={selected}
          selectable={isManager}
          onSelect={(id, isSelected) => {
            setSelected((current) => {
              const next = new Set(current);
              if (isSelected) next.add(id);
              else next.delete(id);
              return next;
            });
          }}
          onSelectAll={(isSelected) =>
            setSelected(isSelected ? new Set(rows.map((row) => String(row['id']))) : new Set())
          }
        />
      )}

      {state.view === 'table' && (
        <Pagination
          page={page?.page ?? 1}
          pageSize={page?.pageSize ?? PAGE_SIZE}
          total={page?.total ?? 0}
          onPage={(next) => setMany({ page: String(next) })}
        />
      )}

      {chooserOpen && (
        <ColumnChooser
          columnIds={columnIds}
          onToggle={toggleColumn}
          onClose={() => setChooserOpen(false)}
        />
      )}

      {bulkOpen && (
        <BulkActionsDialog
          ids={[...selected]}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            setBulkOpen(false);
            setSelected(new Set());
            void active.refetch();
          }}
        />
      )}
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: 'table' | 'board';
  onChange: (view: 'table' | 'board') => void;
}): React.ReactElement {
  return (
    <div className="flex rounded-md border p-0.5">
      <button
        type="button"
        aria-pressed={view === 'table'}
        onClick={() => onChange('table')}
        className={`flex items-center gap-1 rounded px-2 py-1 text-xs ${
          view === 'table' ? 'bg-accent font-medium' : 'text-muted-foreground'
        }`}
      >
        <Table2 className="h-3.5 w-3.5" aria-hidden="true" />
        List
      </button>
      <button
        type="button"
        aria-pressed={view === 'board'}
        onClick={() => onChange('board')}
        className={`flex items-center gap-1 rounded px-2 py-1 text-xs ${
          view === 'board' ? 'bg-accent font-medium' : 'text-muted-foreground'
        }`}
      >
        <KanbanSquare className="h-3.5 w-3.5" aria-hidden="true" />
        Board
      </button>
    </div>
  );
}

/**
 * The CSV of the current filtered view. **An audited egress path** on the
 * server — the audit row records the actor, the filter set and the row count,
 * never the rows — and capped at 5,000, which is also what keeps the response
 * inside the Lambda's 6 MB buffered ceiling.
 */
function ExportButton({ request }: { request: ReturnType<typeof toRequest> }): React.ReactElement {
  const { push } = useToast();
  const [busy, setBusy] = useState(false);

  const run = async (): Promise<void> => {
    setBusy(true);
    try {
      const response = await apiRaw('/api/opportunities/export', {
        method: 'POST',
        body: {
          ...request.filters,
          ...(request.term === null ? {} : { term: request.term }),
        },
      });
      if (!response.ok) {
        push({ tone: 'error', title: 'The export was refused.' });
        return;
      }
      const rowCount = response.headers.get('x-row-count');
      const rowCap = response.headers.get('x-row-cap');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'opportunities.csv';
      anchor.click();
      URL.revokeObjectURL(url);

      push({
        tone: rowCount === rowCap ? 'warning' : 'success',
        title: `${rowCount ?? '?'} rows exported`,
        body:
          rowCount === rowCap
            ? `The export is capped at ${rowCap} rows. Narrow the filters to be sure you have everything.`
            : 'This export is recorded in the audit log — the actor, the filters and the count, never the rows.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={() => void run()} disabled={busy}>
      <Download className="h-3.5 w-3.5" aria-hidden="true" />
      CSV
    </Button>
  );
}

function SavedViewBar({
  savedViews,
  currentQuery,
  myUserId,
  onApply,
  onSave,
  onDelete,
}: {
  savedViews: SavedView[];
  currentQuery: string;
  myUserId: string | null;
  onApply: (query: string) => void;
  onSave: (view: SavedView) => void;
  onDelete: (id: string) => void;
}): React.ReactElement {
  const [naming, setNaming] = useState(false);
  const [label, setLabel] = useState('');

  const builtIns = [
    {
      id: 'my-open',
      label: 'My open opportunities',
      query: myUserId === null ? 'status=open' : `status=open&ownerId=${myUserId}`,
    },
    {
      id: 'ramping',
      label: 'Ramping this year',
      query: `status=open,won&rampBetween=${new Date().getFullYear()}-01-01..${new Date().getFullYear()}-12-31`,
    },
    // `status=all`: this view showed every status before the list defaulted
    // to Open + Won, and won and on-hold deals carry stall signals too.
    { id: 'needs-attention', label: 'Needs attention', query: 'status=all&stalled=any' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-muted-foreground">Views:</span>
      {builtIns.map((view) => (
        <button
          key={view.id}
          type="button"
          onClick={() => onApply(view.query)}
          className="rounded-full border px-2 py-0.5 hover:bg-accent"
        >
          {view.label}
        </button>
      ))}
      {savedViews.map((view) => (
        <span key={view.id} className="inline-flex items-center rounded-full border">
          <button
            type="button"
            onClick={() => onApply(view.query)}
            className="px-2 py-0.5 hover:bg-accent"
          >
            {view.label}
          </button>
          <button
            type="button"
            aria-label={`Delete saved view ${view.label}`}
            onClick={() => onDelete(view.id)}
            className="px-1 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-3 w-3" aria-hidden="true" />
          </button>
        </span>
      ))}

      {naming ? (
        <span className="inline-flex items-center gap-1">
          <Input
            aria-label="Saved view name"
            className="h-7 w-40 text-xs"
            value={label}
            autoFocus
            onChange={(event) => setLabel(event.target.value)}
          />
          <Button
            size="sm"
            className="h-7"
            onClick={() => {
              if (label.trim() === '') return;
              onSave({ id: `local-${Date.now()}`, label: label.trim(), query: currentQuery });
              setLabel('');
              setNaming(false);
            }}
          >
            Save
          </Button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setNaming(true)}
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          title="Saved views are stored in this browser only — there is no saved_views table."
        >
          <Save className="h-3 w-3" aria-hidden="true" />
          Save this view
        </button>
      )}
    </div>
  );
}

function ColumnChooser({
  columnIds,
  onToggle,
  onClose,
}: {
  columnIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
}): React.ReactElement {
  return (
    <Dialog
      open
      onClose={onClose}
      title="Columns"
      description="Stored in this browser, per user."
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <div className="grid grid-cols-2 gap-2">
        {COLUMNS.map((column) => (
          <label key={column.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={columnIds.includes(column.id)}
              onChange={() => onToggle(column.id)}
            />
            {column.label}
          </label>
        ))}
      </div>
    </Dialog>
  );
}

/**
 * Manager bulk actions: reassign the owner, or change the forecast category.
 *
 * Reassignment goes through `POST /:id/transfer`, which is `Manager`/`Admin`
 * only and **always audited** (§2 rule 2) — never through a `PUT` that sets
 * `owner_user_id`, which the API refuses for a `User` and which would not be
 * recorded as a transfer.
 */
function BulkActionsDialog({
  ids,
  onClose,
  onDone,
}: {
  ids: string[];
  onClose: () => void;
  onDone: () => void;
}): React.ReactElement {
  const reference = useReferenceData();
  const { push } = useToast();
  const [ownerId, setOwnerId] = useState('');
  const [forecast, setForecast] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (): Promise<void> => {
    setBusy(true);
    let done = 0;
    const failures: string[] = [];
    for (const id of ids) {
      try {
        if (ownerId !== '') {
          await api(`/api/opportunities/${id}/transfer`, {
            method: 'POST',
            body: { owner_user_id: ownerId, note: 'Bulk reassignment from the list view' },
          });
        }
        if (forecast !== '') {
          await api(`/api/opportunities/${id}`, {
            method: 'PUT',
            body: { forecast_category: forecast },
          });
        }
        done += 1;
      } catch (error) {
        failures.push(error instanceof ApiError ? error.message : 'Unknown error');
      }
    }
    setBusy(false);
    push({
      tone: failures.length === 0 ? 'success' : 'warning',
      title: `${done} of ${ids.length} updated`,
      ...(failures.length === 0 ? {} : { body: failures[0] }),
    });
    onDone();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Bulk actions — ${ids.length} opportunities`}
      description="Reassignment is recorded as a transfer and is always audited."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void run()} disabled={busy || (ownerId === '' && forecast === '')}>
            Apply
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Reassign owner to">
          <Select value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>
            <option value="">— leave unchanged —</option>
            {reference.users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.display_name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Forecast category"
          hint="Forecast categories describe revenue timing, not deal outcome (§6.4). A commit needs a ramp or first-revenue date inside four quarters, or an actual ramp date."
        >
          <Select value={forecast} onChange={(event) => setForecast(event.target.value)}>
            <option value="">— leave unchanged —</option>
            {FORECAST_CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {humanize(value)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}): React.ReactElement | null {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;

  return (
    <div className="flex items-center justify-between text-xs text-muted-foreground">
      <span>
        Page {page} of {pages}
      </span>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
